import { useCallback, useEffect, useRef, useState } from 'react';
import { createPedidoV2, getStrictStockPolicy } from '../../../../../../crud/pedidos_api/pedidos';
import { isFeatureRolloutLocal } from '../../../../../../configuration/directMenuRollout';
import { uuidv4 } from '../../../../../../utils/functions';
import { stockMessages } from './strictStockMessages';

export interface StockRefusal {
  message: string;
  details: Array<{ ingredient_id: number; unit_id: number; missing: string; selection_ids: string[] }>;
}

/** Narrow parser: unknown/network/malformed outcomes are not an accepted order. */
export function stockRefusal(error: unknown): StockRefusal {
  const response = (error as { response?: { data?: unknown } })?.response?.data;
  if (typeof response !== 'object' || response === null) return { message: stockMessages.unknown, details: [] };
  const data = response as Record<string, unknown>;
  const details: StockRefusal['details'] = [];
  if (Array.isArray(data.details)) {
    for (const row of data.details) {
      if (row && Number.isInteger(row.ingredient_id) && Number.isInteger(row.unit_id)
          && typeof row.missing === 'string' && Array.isArray(row.selection_ids)
          && row.selection_ids.every((id: unknown) => typeof id === 'string')) {
        details.push(row);
      }
    }
  }
  return { message: typeof data.message === 'string' ? data.message : stockMessages.unknown, details };
}

/** Presentation gate plus one stable key per unchanged pending basket. */
export function useStrictStock(localId: number | null) {
  const eligible = localId !== null && isFeatureRolloutLocal(localId);
  const [enabled, setEnabled] = useState(false);
  const [refusal, setRefusal] = useState<StockRefusal | null>(null);
  const pending = useRef<{ payload: string; key: string } | null>(null);
  const alive = useRef(true);
  const generation = useRef(0);
  useEffect(() => {
    alive.current = true;
    const current = ++generation.current;
    setEnabled(false);
    setRefusal(null);
    pending.current = null;
    if (eligible) {
      getStrictStockPolicy().then(response => {
        if (alive.current && generation.current === current && response.data.local_id === localId)
          setEnabled(response.data.strict_stock === true);
      }).catch(() => { /* Submit rechecks policy; no false availability is rendered. */ });
    }
    return () => { alive.current = false; generation.current++; };
  }, [eligible, localId]);

  const submit = useCallback(async (payload: unknown) => {
    if (!eligible) return createPedidoV2(payload);
    const current = generation.current;
    let strict = false;
    let policyResolved = false;
    try {
      const policy = await getStrictStockPolicy();
      if (!alive.current || current !== generation.current)
        throw Object.assign(new Error('Disposed stock submission'), { cancelled: true });
      if (policy.data.local_id !== localId || typeof policy.data.strict_stock !== 'boolean')
        throw new Error(stockMessages.policy);
      strict = policy.data.strict_stock;
      policyResolved = true;
      if (alive.current && current === generation.current) setEnabled(strict);
      if (!strict) return await createPedidoV2(payload);
      const serialized = JSON.stringify(payload);
      if (!pending.current || pending.current.payload !== serialized)
        pending.current = { payload: serialized, key: uuidv4() };
      const response = await createPedidoV2(payload, pending.current.key);
      if (!alive.current || current !== generation.current)
        throw Object.assign(new Error('Disposed stock submission'), { cancelled: true });
      if (typeof response.status !== 'number' || response.status < 200 || response.status >= 300 || response.data?.strict_stock !== true
          || !Number.isSafeInteger(response.data?.pedido_id))
        throw new Error(stockMessages.unknown);
      if (alive.current && current === generation.current) {
        pending.current = null;
        setRefusal(null);
      }
      return response;
    } catch (error) {
      if ((error as { cancelled?: boolean })?.cancelled)
        throw Object.assign(new Error('Disposed stock submission'), { strictStock: true, cancelled: true });
      // Policy failure is uncertain too; never fall through to permissive submit.
      if (strict || !policyResolved) {
        if (alive.current && current === generation.current) setRefusal(stockRefusal(error));
        throw Object.assign(new Error(stockMessages.unknown), { strictStock: true, cause: error });
      }
      throw error;
    }
  }, [eligible, enabled, localId]);
  return { enabled, refusal, submit };
}
